(() => {
  const selectors = {
    root: '[data-ctr-best-sellers]',
    tab: '.ctr-best-sellers__tab',
    panel: '.ctr-best-sellers__panel',
    carousel: 'ctr-best-sellers-carousel',
    controls: '[data-controls]',
    prevBtn: '[data-prev]',
    nextBtn: '[data-next]',
  };

  /** Custom element for each carousel panel */
  class CtrBestSellersCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this._resizeObserver = null;
    }

    connectedCallback() {
      this.initCarousel();
      this.bindNavigation();
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    get slidesPerView() {
      return parseFloat(this.getAttribute('data-slides-per-view')) || 1.4;
    }

    get slidesPerViewDesktop() {
      return parseInt(this.getAttribute('data-slides-per-view-desktop'), 10) || 4;
    }

    get spaceBetween() {
      return parseInt(this.getAttribute('data-space-between'), 10) || 16;
    }

    get spaceBetweenDesktop() {
      return parseInt(this.getAttribute('data-space-between-desktop'), 10) || 24;
    }

    get loop() {
      return this.getAttribute('data-loop') === 'true';
    }

    get autoplay() {
      return this.getAttribute('data-autoplay') === 'true';
    }

    get autoplayDelay() {
      return parseInt(this.getAttribute('data-autoplay-delay'), 10) || 5000;
    }

    getConfig() {
      const self = this;
      const config = {
        slidesPerView: this.slidesPerView,
        spaceBetween: this.spaceBetween,
        loop: this.loop,
        breakpoints: {
          750: {
            slidesPerView: this.slidesPerViewDesktop,
            spaceBetween: this.spaceBetweenDesktop,
          },
        },
        on: {
          init: function () {
            self.updateControlsVisibility(this);
          },
          resize: function () {
            self.updateControlsVisibility(this);
          },
          breakpoint: function () {
            self.updateControlsVisibility(this);
          },
        },
      };

      // Pagination
      const paginationEl = this.querySelector('.swiper-pagination');
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }

      // Autoplay
      if (this.autoplay) {
        config.autoplay = {
          delay: this.autoplayDelay,
          disableOnInteraction: false,
        };
      }

      return config;
    }

    initCarousel() {
      if (typeof Swiper === 'undefined') return;

      const swiperEl = this.querySelector('.js-ctr-best-sellers-swiper');
      if (!swiperEl) return;

      this.swiper = new Swiper(swiperEl, this.getConfig());
    }

    bindNavigation() {
      const prevBtn = this.querySelector(selectors.prevBtn);
      const nextBtn = this.querySelector(selectors.nextBtn);

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          if (this.swiper) this.swiper.slidePrev();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          if (this.swiper) this.swiper.slideNext();
        });
      }
    }

    /** Hide controls bar when all slides are visible */
    updateControlsVisibility(swiperInstance) {
      const controlsEl = this.querySelector(selectors.controls);
      if (!controlsEl) return;

      const totalSlides = swiperInstance.slides
        ? swiperInstance.slides.length
        : 0;
      const perView = swiperInstance.params.slidesPerView;

      if (totalSlides <= perView) {
        controlsEl.classList.add('is-hidden');
      } else {
        controlsEl.classList.remove('is-hidden');
      }
    }
  }

  if (!customElements.get('ctr-best-sellers-carousel')) {
    customElements.define('ctr-best-sellers-carousel', CtrBestSellersCarousel);
  }

  /** Tab switching */
  const initBestSellers = (container) => {
    if (!container || container.dataset.ctrBestSellersReady === 'true') return;

    const tabs = Array.from(container.querySelectorAll(selectors.tab));
    const panels = Array.from(container.querySelectorAll(selectors.panel));

    if (!tabs.length || !panels.length) return;

    const setActiveTab = (index) => {
      tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;

        // Re-init / update swiper for newly shown panel
        if (isActive) {
          const carouselEl = panel.querySelector(selectors.carousel);
          if (carouselEl && carouselEl.swiper) {
            carouselEl.swiper.update();
            carouselEl.updateControlsVisibility(carouselEl.swiper);
          } else if (carouselEl && !carouselEl.swiper) {
            carouselEl.initCarousel();
            carouselEl.bindNavigation();
          }
        }
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        setActiveTab(Number(tab.dataset.tabIndex || 0));
      });
    });

    container.dataset.ctrBestSellersReady = 'true';

    const initialIndex = tabs.findIndex((tab) => tab.classList.contains('is-active'));
    setActiveTab(initialIndex >= 0 ? initialIndex : 0);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(selectors.root).forEach(initBestSellers);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
